import {
  afterEveryRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Header } from '@spirit-testing/component-library';
import { filter, map } from 'rxjs';
import { Membership } from '../../models/membership.model';
import { UserProfileDTO } from '../../models/user-profile.model';
import { isTenantAdmin, USER_ROLE } from '../../models/user-role.model';
import { AvatarComponent } from '../avatar/avatar.component';

export type UserMenuProfile = UserProfileDTO | 'loading' | 'error';

interface UserMenuEntry {
  path: string;
  labelKey: string;
  testId: string;
  icon: 'account' | 'settings';
}

const ACCOUNT_ENTRY: UserMenuEntry = {
  path: '/account',
  labelKey: 'userMenu.account',
  testId: 'usermenu-konto',
  icon: 'account',
};
const ADMINISTRATION_ENTRY: UserMenuEntry = {
  path: '/settings',
  labelKey: 'userMenu.administration',
  testId: 'usermenu-verwaltung',
  icon: 'settings',
};

const TENANT_ROLE_KEYS: Record<string, string> = {
  [USER_ROLE.PLATFORM_ADMIN]: 'userMenu.role.platformAdmin',
  [USER_ROLE.TENANT_ADMIN]: 'userMenu.role.tenantAdmin',
};

const MENU_OFFSET_TOP_PX = 52;

@Component({
  selector: 'app-user-menu',
  imports: [AvatarComponent, TranslatePipe, MatIcon],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(click)': 'keepOpenOnClickOutsideItems($event)',
    '(document:keydown.escape)': 'closeOnEscape()',
    '(window:resize)': 'placeMenu()',
  },
})
export class UserMenuComponent {
  private readonly header = inject(Header);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly router = inject(Router);

  readonly profile = input<UserMenuProfile>('loading');
  readonly memberships = input<Membership[] | null>(null);

  protected readonly loadedProfile = computed(() => {
    const profile = this.profile();
    return typeof profile === 'object' ? profile : null;
  });
  protected readonly showHead = computed(() => this.profile() !== 'error');
  protected readonly tenantRoleKey = computed(
    () => TENANT_ROLE_KEYS[this.loadedProfile()?.role ?? ''] ?? 'userMenu.role.user',
  );
  protected readonly projectRoleKey = computed(() => {
    const memberships = this.memberships();
    if (memberships === null) return null;
    return memberships.some((m) => m.role === 'PROJECT_ADMIN')
      ? 'userMenu.role.projectLead'
      : 'userMenu.role.contributor';
  });
  protected readonly entries = computed(() =>
    isTenantAdmin(this.loadedProfile()?.role) ? [ACCOUNT_ENTRY, ADMINISTRATION_ENTRY] : [ACCOUNT_ENTRY],
  );

  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  private readonly activePath = computed(() => this.currentPath().split(/[?#]/)[0]);

  private open = false;
  private triggerPrepared = false;

  constructor() {
    afterEveryRender(() => this.syncWithHeader());
  }

  protected isActive(path: string): boolean {
    const current = this.activePath();
    return current === path || current.startsWith(`${path}/`);
  }

  protected select(path: string): void {
    void this.router.navigateByUrl(path);
  }

  protected onMenuKeydown(event: KeyboardEvent): void {
    const items = this.menuItems();
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const last = items.length - 1;
    const targets: Record<string, number> = {
      ArrowDown: index >= last ? 0 : index + 1,
      ArrowUp: index <= 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    if (event.key in targets) {
      event.preventDefault();
      items[targets[event.key]]?.focus();
    } else if (event.key === 'Tab') {
      // Erst nach dem Fokuswechsel des Browsers schließen, sonst springt Tab vom Auslöser aus weiter.
      setTimeout(() => this.closeFromInside());
    }
  }

  protected keepOpenOnClickOutsideItems(event: MouseEvent): void {
    if (!(event.target as Element).closest('[role="menuitem"]')) {
      event.stopPropagation();
    }
  }

  protected closeOnEscape(): void {
    if (!this.open) return;
    this.closeFromInside();
    this.trigger()?.focus();
  }

  protected placeMenu(): void {
    const trigger = this.trigger();
    const menu = this.menuElement();
    if (!this.open || !trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    this.renderer.setStyle(menu, 'top', `${rect.top + MENU_OFFSET_TOP_PX}px`);
    this.renderer.setStyle(menu, 'right', `${document.documentElement.clientWidth - rect.right}px`);
  }

  private syncWithHeader(): void {
    const trigger = this.trigger();
    if (!trigger) return;
    if (!this.triggerPrepared) {
      this.renderer.setAttribute(trigger, 'aria-haspopup', 'menu');
      this.renderer.setAttribute(trigger, 'data-testid', 'usermenu-trigger');
      this.triggerPrepared = true;
    }
    this.renderer.setAttribute(trigger, 'aria-expanded', String(this.header.showMenu));
    // Erst handeln, wenn der Header sein @if (showMenu) gerendert hat: Fokus braucht das Menü im DOM.
    const open = !!this.menuElement()?.isConnected;
    if (open !== this.header.showMenu || open === this.open) return;
    this.open = open;
    if (open) {
      this.placeMenu();
      this.menuItems()[0]?.focus();
    } else if (this.focusIsLost()) {
      trigger.focus();
    }
  }

  private closeFromInside(): void {
    if (this.header.showMenu) {
      this.trigger()?.click();
    }
  }

  private focusIsLost(): boolean {
    const active = document.activeElement;
    return !active || active === document.body || !active.isConnected;
  }

  private trigger(): HTMLButtonElement | null {
    return this.header.menu?.nativeElement.querySelector('button.header__profile') ?? null;
  }

  private menuElement(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('[role="menu"]');
  }

  private menuItems(): HTMLButtonElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
  }
}
